import React, { useCallback, useEffect, useState } from 'react';
import { PodiatryConditionalOptionsType } from 'types/types';
import {
  ActiveTypeOfReasonForVisit,
  usePersistRFVReducer,
} from 'reducer/reasonForVisit';
import styles from 'components/podiatry/page.module.scss';
import MetaTag from 'components/meta';
import { StateType } from 'reducer';
import InputText from 'components/common/InputText';
import Button from 'components/common/button';
import { useScrollIntoView } from 'hooks/useScroll';
import { isEmptyString } from 'utils/helper';
import PainRate from 'components/common/painRate';
import clsx from 'clsx';

type Props = {
  options: PodiatryConditionalOptionsType[] | PodiatryConditionalOptionsType;
  nextPage: number;
  currentPage?: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function WhatTypeOfPainScreen({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  isFilter = false,
}: Props) {
  const { errorTagRefs, scrollToError } = useScrollIntoView(2);
  const [inputText, setInputText] = useState('');
  const [inputTextError, setInputTextError] = useState(false);
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionItems, setQuestionItems] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [painRateError, setPainRateError] = useState<boolean>(false);

  const [painRate, setPainRate] = useState<string>('');

  const [state, dispatch] = usePersistRFVReducer();

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: PodiatryConditionalOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        try {
          setQuestionItems(findOptions.options);
          setTitle(findOptions.title);
          setSubTitle(findOptions.subTitle);
        } catch (error) {
          console.error('🚀 ~ Error on filter options', error);
        }
      }
    } else if (!Array.isArray(options)) {
      setQuestionItems(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
  }, []);

  useEffect(() => {
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        setSelectedItems(data.value);
        if (data.painRate) setPainRate(data.painRate);
        if (data.text) setInputText(data.text);
      }
    }
  }, []);

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      if (item === 'None') {
        setSelectedItems([item]);
        setPainRate('0');
      } else {
        setSelectedItems((old) => {
          if (old.toString() === 'None') {
            return [item];
          } if (old.find((value) => value === item)) {
            return old.filter((data) => data !== item);
          } return [...old, item];
        });
      }
    } else {
      setSelectedItems([item]);
    }

    setError(false);
  }, []);

  useEffect(() => {
    const findNone = selectedItems.find((item) => item !== 'None');
    if (painRate === '0' && findNone) {
      setPainRate('');
    }
  }, [selectedItems]);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (selectedItems.length === 0) {
      setError(true);
      scrollToError(0);
      return;
    }

    if (isEmptyString(painRate)) {
      scrollToError(1);
      setPainRateError(true);
      return;
    }

    const dispatchPayload: ActiveTypeOfReasonForVisit = {
      type: actionPage,
      name: title,
      value: selectedItems,
      painRate,
      text: '',
    };

    const other = selectedItems.find((item) => item === 'Other');
    if (other) {
      if (!inputText.trim()) {
        setInputTextError(true);
        return;
      }
    }

    if (inputText && other) {
      dispatchPayload.text = inputText;
    }
    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
    setInputTextError(false);
  };

  const onChangePain = (pain: string) => {
    setPainRate(pain);
    if (pain === '0') {
      setSelectedItems(['None']);
    } else if (selectedItems.toString() === 'None') {
      setSelectedItems([]);
    }
    setPainRateError(false);
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 ref={errorTagRefs[0]} className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && (
        <p className="error-message">
          Please select at least one option.
        </p>
      )}

      <div>
        {questionItems.map((option) => {
          const selectedClass = selectedItems.find(
            (item) => item === option,
          );
          return (
            <div key={option}>
              <p
                className={clsx('option', { 'selected-option': selectedClass })}
                onClick={() => onSelectOptions(option)}
                onKeyUp={() => {}}
              >
                {option}
              </p>
              {option === 'Other' && selectedClass && (
                <InputText
                  value={inputText}
                  placeholder="Type..."
                  onChange={onChangeText}
                  error={inputTextError}
                  maxLength={160}
                  isWidthFull
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <PainRate
          errorRef={errorTagRefs[1]}
          value={painRate}
          onChange={onChangePain}
          hasError={painRateError}
        />
      </div>
      <div className="next-button-fixed">
        <Button onClick={() => clickNextOrSubmit(nextPage)}>
          Next
        </Button>
      </div>
    </>
  );
}

export default WhatTypeOfPainScreen;
